import { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, XCircle, Loader, Download, ShoppingBag } from 'lucide-react';

const PaymentVerify = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('loading'); // loading, success, failed
  const [orderData, setOrderData] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    const verifyPayment = async () => {
      const reference = searchParams.get('reference');
      console.log('Payment verification started, reference:', reference);

      if (!reference) {
        console.log('No reference found in URL');
        setStatus('failed');
        setError('No payment reference found');
        return;
      }

      try {
        console.log('Calling verify endpoint...');
        const response = await fetch(`/.netlify/functions/order-verify?reference=${reference}`);
        const data = await response.json();
        console.log('Verify response:', data);

        if (data.success) {
          console.log('Payment verified successfully');
          setStatus('success');
          setOrderData(data.data);
        } else {
          console.log('Verification failed:', data.message);
          setStatus('failed');
          setError(data.message || 'Payment verification failed');
        }
      } catch (err) {
        console.error('Verification error:', err);
        setStatus('failed');
        setError('An error occurred while verifying your payment');
      }
    };

    verifyPayment();
  }, [searchParams]);

  if (status === 'loading') {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-900 via-gray-800 to-gray-900 text-white flex items-center justify-center px-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center"
        >
          <Loader size={64} className="text-emerald-400 animate-spin mx-auto mb-6" />
          <h2 className="text-2xl font-bold mb-2">Verifying Payment...</h2>
          <p className="text-gray-400">Please wait while we confirm your transaction</p>
        </motion.div>
      </div>
    );
  }

  if (status === 'success') {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-900 via-gray-800 to-gray-900 text-white flex items-center justify-center px-4 py-20">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-2xl w-full"
        >
          <div className="bg-gray-800 rounded-lg p-8 shadow-xl text-center">
            <div className="w-20 h-20 bg-emerald-500 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle size={48} className="text-white" />
            </div>

            <h1 className="text-3xl font-bold mb-4">Payment Successful!</h1>
            <p className="text-gray-400 mb-8">
              Thank you for your purchase. Your payment has been confirmed.
            </p>

            {orderData && (
              <div className="bg-gray-700 rounded-lg p-6 mb-8 text-left">
                <h3 className="text-xl font-semibold mb-4">Order Details</h3>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-400">Order Number:</span>
                    <span className="font-semibold">{orderData.order.order_number}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Amount Paid:</span>
                    <span className="font-semibold text-emerald-400">
                      GHS {parseFloat(orderData.order.total_amount).toLocaleString()}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Status:</span>
                    <span className="px-3 py-1 bg-emerald-500/20 text-emerald-400 rounded-full text-sm">
                      Completed
                    </span>
                  </div>
                </div>

                {orderData.order.items && orderData.order.items.length > 0 && (
                  <div className="mt-6 pt-6 border-t border-gray-600">
                    <h4 className="font-semibold mb-3">Items Purchased:</h4>
                    <ul className="space-y-2">
                      {orderData.order.items.map((item) => (
                        <li key={item.id} className="flex justify-between">
                          <span>{item.product_name}</span>
                          <span className="text-gray-400">
                            GHS {parseFloat(item.price).toLocaleString()}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            <div className="flex gap-4 justify-center">
              <Link
                to="/dashboard"
                className="inline-flex items-center gap-2 px-6 py-3 bg-emerald-500 hover:bg-emerald-600 rounded-lg font-semibold transition-colors"
              >
                <Download size={20} />
                Go to Dashboard
              </Link>
              <Link
                to="/shop"
                className="inline-flex items-center gap-2 px-6 py-3 bg-gray-700 hover:bg-gray-600 rounded-lg font-semibold transition-colors"
              >
                <ShoppingBag size={20} />
                Continue Shopping
              </Link>
            </div>

            <p className="mt-8 text-sm text-gray-500">
              A confirmation email has been sent to your email address.
              <br />
              You can download your purchase from your dashboard.
            </p>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 via-gray-800 to-gray-900 text-white flex items-center justify-center px-4 py-20">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-2xl w-full"
      >
        <div className="bg-gray-800 rounded-lg p-8 shadow-xl text-center">
          <div className="w-20 h-20 bg-red-500 rounded-full flex items-center justify-center mx-auto mb-6">
            <XCircle size={48} className="text-white" />
          </div>

          <h1 className="text-3xl font-bold mb-4">Payment Failed</h1>
          <p className="text-gray-400 mb-8">{error}</p>

          <div className="flex gap-4 justify-center">
            <Link
              to="/shop"
              className="inline-flex items-center gap-2 px-6 py-3 bg-emerald-500 hover:bg-emerald-600 rounded-lg font-semibold transition-colors"
              >
              <ShoppingBag size={20} />
              Back to Shop
            </Link>
          </div>

          <p className="mt-8 text-sm text-gray-500">
            If you believe this is an error, please contact support.
          </p>
        </div>
      </motion.div>
    </div>
  );
};

export default PaymentVerify;
